import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './AggregateResequence.module.css';
import clsx from 'clsx';
import { AuthChecker, Button, SearchBar } from '../../atomicComponents';
import AggregateResequenceTable from '../AggregateResequenceTable/AggregateResequenceTable';
import { SelectionMenu } from '../../atomicComponents';
import LeftArrowIcon from '@material-ui/icons/KeyboardArrowLeft';
import RightArrowIcon from '@material-ui/icons/KeyboardArrowRight';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import { PANTNAGAR_PLANT_NAME } from '../../constants';

const AggregateResequence = ({
	rows: data,
	columns: originalColumns,
	rowHeight = 38,
	defaultColumns: originalDefaultColumns,
	primaryAction,
	secondaryActions = [],
}) => {
	const [sourceList, setSourceList] = useState([...data]);
	const [destinationList, setDestinationList] = useState([]);
	const [plant] = usePlantAndLinesStore().plantState;

	const [partNumberSearch, setPartNumberSearch] = useState('');
	const [quantitySearch, setQuantitySearch] = useState('');

	const getFilteredSourceList = () => {
		if (plant.label !== PANTNAGAR_PLANT_NAME) return sourceList;
		let filtered = sourceList;
		if (partNumberSearch) {
			filtered = filtered.filter(row =>
				row.materialNumber?.toString().toLowerCase().includes(partNumberSearch.toLowerCase())
			);
		}
		if (quantitySearch) {
			const qty = parseInt(quantitySearch);
			if (!isNaN(qty) && qty > 0) {
				filtered = filtered.slice(0, qty);
			}
		}
		return filtered;
	};

	const filteredSourceList = getFilteredSourceList();

	const [fieldToFiltersSetMapSource, setFieldToFiltersSetMapSource] = useState(
		{},
	);
	const [
		fieldToFiltersSetMapDestination,
		setFieldToFiltersSetMapDestination,
	] = useState({});

	const [selectedColumns, setSelectedColumns] = useState(
		originalDefaultColumns,
	);
	const [isCustomizeTableMenuOpen, setIsCustomizeTableMenuOpen] = useState(
		false,
	);

	const customizeTableButtonRef = useRef(null);

	const handleColumnSelectionChange = (columnTitle) => {
		if (selectedColumns.map(({ title }) => title).includes(columnTitle))
			return setSelectedColumns((prev) =>
				prev.filter((column) => column.title !== columnTitle),
			);
		return setSelectedColumns((prev) => [
			...prev,
			...originalColumns.filter(({ title }) => title === columnTitle),
		]);
	};

	const applyColumnsSelections = (columnSelections) => {
		setSelectedColumns([
			...selectedColumns.slice(
				0,
				selectedColumns.length - originalDefaultColumns.length,
			),
			...originalColumns.filter((col) => columnSelections.includes(col.title)),
		]);
	};

	const moveSelectedIntoDestination = () => {
		const checkRemovedSelectedRows = sourceList
			.filter((row) => row.isSelected)
			.map(({ isSelected, ...rest }) => ({ ...rest }));

		setSourceList((prev) => prev.filter((row) => !row.isSelected));
		setDestinationList((prev) => [...prev, ...checkRemovedSelectedRows]);
	};

	const moveSelectedIntoSource = () => {
		const checkRemovedSelectedRows = destinationList
			.filter((row) => row.isSelected)
			.map(({ isSelected, ...rest }) => ({ ...rest }));

		setDestinationList((prev) => prev.filter((row) => !row.isSelected));
		setSourceList((prev) => [...prev, ...checkRemovedSelectedRows]);
	};

	const handleRowSelectForListCurry = (setListData) => (id) => {
		setListData((prevState) =>
			prevState.map((row) =>
				row.id === id ? { ...row, isSelected: !row.isSelected } : row,
			),
		);
	};

	const handleAllRowsSelectForListCurry = (setListData) => (idSet) => {
		setListData((prevState) => {
			let cumulativeCheckState = prevState
				.filter((row) => idSet.has(row.id))
				.every((row) => row.isSelected);

			return prevState.map((row) =>
				idSet.has(row.id)
					? {
						...row,
						isSelected: !cumulativeCheckState,
					}
					: row,
			);
		});
	};

	useEffect(() => {
		setSourceList([...data]);
		setDestinationList([]);
	}, [data, data.length]);

	return (
		<>
			{plant.label === PANTNAGAR_PLANT_NAME && (
				<div className={styles.searchContainer}>
					<div className={styles.searchItem}>
						<SearchBar
							value={partNumberSearch}
							onChange={setPartNumberSearch}
							placeholder="Search Part Number"
						/>
					</div>
					<div className={styles.searchItem}>
						<SearchBar
							value={quantitySearch}
							onChange={setQuantitySearch}
							placeholder="Quantity"
						/>
					</div>
				</div>
			)}
			<div className={styles.tablesArea}>
				<div className={styles.tableContainer}>
					<div className={styles.tableView} data-testid='left-list'>
						<AggregateResequenceTable
							listId="left-list"
							columns={selectedColumns}
							defaultColumns={selectedColumns}
							rowHeight={rowHeight}
							rows={filteredSourceList}
							setFieldToFiltersSetMap={setFieldToFiltersSetMapSource}
							fieldToFiltersSetMap={fieldToFiltersSetMapSource}
							setSelectedColumns={setSelectedColumns}
							handleRowSelection={handleRowSelectForListCurry(setSourceList)}
							handleAllRowsSelect={handleAllRowsSelectForListCurry(
								setSourceList,
							)}
						/>
					</div>
				</div>
				<div className={styles.transferButtonsContainer}>
					<Button
						variant="primary"
						className={styles.transferButton}
						disabled={!sourceList.length}
						onClick={moveSelectedIntoDestination}
						data-testid='transfer-right-button'
					>
						<RightArrowIcon />
					</Button>
					<Button
						variant="primary"
						className={styles.transferButton}
						disabled={!destinationList.length}
						onClick={moveSelectedIntoSource}
						data-testid='transfer-left-button'
					>
						<LeftArrowIcon />
					</Button>
				</div>
				<div className={styles.tableContainer}>
					<div className={styles.tableView} data-testid='right-list'>
						<AggregateResequenceTable
							listId="right-list"
							columns={selectedColumns}
							defaultColumns={selectedColumns}
							rowHeight={rowHeight}
							rows={destinationList}
							setFieldToFiltersSetMap={setFieldToFiltersSetMapDestination}
							fieldToFiltersSetMap={fieldToFiltersSetMapDestination}
							setSelectedColumns={setSelectedColumns}
							renderPlaceholder={() => (
								<div className={styles.placeholderContainer}>
									<p>
										To create your own sequence please select the Part from left and use arrow to move to the right
									</p>
								</div>
							)}
							handleRowSelection={handleRowSelectForListCurry(
								setDestinationList,
							)}
							handleAllRowsSelect={handleAllRowsSelectForListCurry(
								setDestinationList,
							)}
						/>
					</div>
				</div>
			</div>

			<div className={styles.actionsBar}>
				<Button
					className={styles.actionButton}
					variant="tertiary"
					onClick={() => setIsCustomizeTableMenuOpen(true)}
					ref={customizeTableButtonRef}
				>
					CUSTOMIZE TABLE
				</Button>
				{secondaryActions.map((action) => (
					<AuthChecker operation={action.authOperation}>
						{(isAuthorized) => (
							<Button
								variant="primary"
								className={styles.actionButton}
								style={{
									backgroundColor: !(
										action.authOperation &&
										(!isAuthorized || !action.shouldEnable(destinationList))
									)
										? action.backgroundColor
										: null,
								}}
								onClick={() => action.actionFn(destinationList)}
								disabled={
									action.authOperation &&
									(!isAuthorized || !action.shouldEnable(destinationList))
								}
							>
								{action.name}
							</Button>
						)}
					</AuthChecker>
				))}
				{(Object.values(fieldToFiltersSetMapSource).every(
					(filtersSet) => !filtersSet?.size,
				) &&
					Object.values(fieldToFiltersSetMapDestination).every(
						(filtersSet) => !filtersSet?.size,
					)) || (
						<Button
							className={clsx(styles.actionButton, styles.clearFiltersButton)}
							variant="secondary"
							disabled={
								Object.values(fieldToFiltersSetMapSource).every(
									(filtersSet) => !filtersSet?.size,
								) &&
								Object.values(fieldToFiltersSetMapDestination).every(
									(filtersSet) => !filtersSet?.size,
								)
							}
							onClick={() => {
								setFieldToFiltersSetMapSource({});
								setFieldToFiltersSetMapDestination({});
							}}
						>
							Clear Filters
						</Button>
					)}
				{primaryAction && (
					<AuthChecker operation={primaryAction.authOperation}>
						{(isAuthorized) => (
							<Button
								className={clsx(
									styles.actionButton,
									styles.primaryActionButton,
								)}
								variant="primary"
								onClick={() => primaryAction.actionFn(destinationList)}
								disabled={
									!isAuthorized || !primaryAction.shouldEnable(destinationList)
								}
								data-testid="primary-action"
							>
								{primaryAction.name}
							</Button>
						)}
					</AuthChecker>
				)}
			</div>
			<SelectionMenu
				anchorEl={customizeTableButtonRef.current}
				open={isCustomizeTableMenuOpen}
				handleClose={() => setIsCustomizeTableMenuOpen(false)}
				options={originalColumns.map(({ title }) => title)}
				currentSelections={selectedColumns.map(({ title }) => title)}
				handleChange={handleColumnSelectionChange}
				resetToDefault={() => setSelectedColumns([...selectedColumns])}
				applySelections={applyColumnsSelections}
				defaultColumns={selectedColumns.map(({ title }) => title)}
			/>
		</>
	);
};

AggregateResequence.propTypes = {
	rows: PropTypes.array,
	columns: PropTypes.array,
	rowHeight: PropTypes.number,
	defaultColumns: PropTypes.array,
	primaryAction: PropTypes.object,
	secondaryActions: PropTypes.array,
};

export default AggregateResequence;
